import { AlertTriangle, Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { apiClient, type CustomPrompt } from '@/api';
import {
  activeCustomPromptIdState,
  customPromptsState
} from '@/state/project';

type Props = {
  onActiveBodyChange: (body: string) => void;
};

type EditorState =
  | { mode: 'idle' }
  | { mode: 'create' }
  | { mode: 'edit'; prompt: CustomPrompt };

const PREVIEW_CHARS = 160;
const NEEDS_PLACEHOLDER = /\{question\}/;

export default function CustomPromptManager({ onActiveBodyChange }: Props) {
  const [prompts, setPrompts] = useRecoilState(customPromptsState);
  const [activeId, setActiveId] = useRecoilState(activeCustomPromptIdState);
  const [editor, setEditor] = useState<EditorState>({ mode: 'idle' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .listCustomPrompts()
      .then((res) => {
        if (cancelled) return;
        setPrompts(res.prompts || []);
        setActiveId(res.active_id || null);
        if (res.active_id) {
          const active = (res.prompts || []).find((p) => p.id === res.active_id);
          if (active) onActiveBodyChange(active.body);
        }
      })
      .catch((err) => {
        console.error('[CustomPromptManager] failed to load prompts', err);
        toast.error('Could not load saved prompts.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [setPrompts, setActiveId, onActiveBodyChange]);

  const handleCreate = async (name: string, body: string) => {
    try {
      const created = await apiClient.createCustomPrompt(name, body);
      setPrompts((prev) => [...prev, created]);
      setEditor({ mode: 'idle' });
      toast.success(`Saved "${created.name}".`);
    } catch (err) {
      console.error(err);
      toast.error('Could not save prompt.');
    }
  };

  const handleUpdate = async (id: string, name: string, body: string) => {
    try {
      const updated = await apiClient.updateCustomPrompt(id, { name, body });
      setPrompts((prev) => prev.map((p) => (p.id === id ? updated : p)));
      if (activeId === id) onActiveBodyChange(updated.body);
      setEditor({ mode: 'idle' });
      toast.success(`Updated "${updated.name}".`);
    } catch (err) {
      console.error(err);
      toast.error('Could not update prompt.');
    }
  };

  const handleDelete = async (prompt: CustomPrompt) => {
    if (!window.confirm(`Delete "${prompt.name}"? This cannot be undone.`)) return;
    try {
      await apiClient.deleteCustomPrompt(prompt.id);
      setPrompts((prev) => prev.filter((p) => p.id !== prompt.id));
      if (activeId === prompt.id) {
        setActiveId(null);
        onActiveBodyChange('');
      }
      toast.success(`Deleted "${prompt.name}".`);
    } catch (err) {
      console.error(err);
      toast.error('Could not delete prompt.');
    }
  };

  const handleActivate = async (prompt: CustomPrompt | null) => {
    const targetId = prompt ? prompt.id : null;
    try {
      await apiClient.setActiveCustomPrompt(targetId);
      setActiveId(targetId);
      onActiveBodyChange(prompt ? prompt.body : '');
      toast.success(prompt ? `Using "${prompt.name}".` : 'Reverted to preset.');
    } catch (err) {
      console.error(err);
      toast.error('Could not switch active prompt.');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-foreground">Custom Prompts</h3>
          <p className="text-xs text-muted-foreground">
            Save reusable prompt templates. The active one replaces the preset on every
            chat until you stop using it.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setEditor({ mode: 'create' })}
          disabled={editor.mode === 'create'}
        >
          <Plus className="h-4 w-4 mr-1" />
          New prompt
        </Button>
      </div>

      {editor.mode === 'create' && (
        <PromptEditor
          onCancel={() => setEditor({ mode: 'idle' })}
          onSubmit={handleCreate}
        />
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : prompts.length === 0 && editor.mode !== 'create' ? (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            No custom prompts yet — save one to stop rewriting the same instructions
            every chat.
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {prompts.map((prompt) => {
            if (editor.mode === 'edit' && editor.prompt.id === prompt.id) {
              return (
                <PromptEditor
                  key={prompt.id}
                  initialName={prompt.name}
                  initialBody={prompt.body}
                  onCancel={() => setEditor({ mode: 'idle' })}
                  onSubmit={(name, body) => handleUpdate(prompt.id, name, body)}
                />
              );
            }
            return (
              <PromptCard
                key={prompt.id}
                prompt={prompt}
                active={prompt.id === activeId}
                onActivate={() => handleActivate(prompt)}
                onDeactivate={() => handleActivate(null)}
                onEdit={() => setEditor({ mode: 'edit', prompt })}
                onDelete={() => handleDelete(prompt)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

type PromptCardProps = {
  prompt: CustomPrompt;
  active: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

function PromptCard({
  prompt,
  active,
  onActivate,
  onDeactivate,
  onEdit,
  onDelete
}: PromptCardProps) {
  const preview =
    prompt.body.length > PREVIEW_CHARS
      ? prompt.body.slice(0, PREVIEW_CHARS).trimEnd() + '…'
      : prompt.body;
  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-foreground truncate">
            {prompt.name}
          </p>
          {active && (
            <Badge className="text-[10px] uppercase tracking-wide">Active</Badge>
          )}
        </div>
        <pre className="whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">
          {preview || '(empty)'}
        </pre>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {active ? (
            <Button variant="outline" size="sm" onClick={onDeactivate}>
              <X className="h-4 w-4 mr-1" />
              Stop using
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={onActivate}>
              <Check className="h-4 w-4 mr-1" />
              Use
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

type PromptEditorProps = {
  initialName?: string;
  initialBody?: string;
  onCancel: () => void;
  onSubmit: (name: string, body: string) => void;
};

function PromptEditor({
  initialName = '',
  initialBody = '',
  onCancel,
  onSubmit
}: PromptEditorProps) {
  const [name, setName] = useState(initialName);
  const [body, setBody] = useState(initialBody);

  const trimmedName = name.trim();
  const trimmedBody = body.trim();
  const missingPlaceholder = trimmedBody.length > 0 && !NEEDS_PLACEHOLDER.test(body);
  const canSubmit = trimmedName.length > 0 && trimmedBody.length > 0;

  return (
    <Card className="border-primary/40">
      <CardContent className="p-4 flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="custom-prompt-name"
            className="text-xs font-medium text-muted-foreground"
          >
            Name
          </label>
          <Input
            id="custom-prompt-name"
            value={name}
            placeholder="e.g. Red team deep dive"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="custom-prompt-body"
            className="text-xs font-medium text-muted-foreground"
          >
            Template
          </label>
          <Textarea
            id="custom-prompt-body"
            rows={8}
            value={body}
            placeholder={'You are a ...\n\n{question}'}
            onChange={(e) => setBody(e.target.value)}
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            <code className="font-mono">{'{question}'}</code> is replaced with the
            user message at send time.
          </p>
          {missingPlaceholder && (
            <p className="flex items-center gap-1 text-xs text-destructive">
              <AlertTriangle className="h-3.5 w-3.5" />
              Template is missing <code className="font-mono">{'{question}'}</code> —
              the user message will be appended at the end.
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={() => onSubmit(trimmedName, body)}
          >
            Save prompt
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
